import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtN, fmtRelative, cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type Row = {
  id: string;
  kind: 'trip' | 'vibe';
  tripId: string | null;
  userId: string;
  caption: string | null;
  coverImageUrl: string | null;
  likeCount: number;
  commentCount: number;
  publishedAt: string;
  authorEmail: string | null;
  authorName: string | null;
};
type Env = { rows: Row[]; page: number; pageSize: number; total: number };

const KINDS: Array<'' | 'trip' | 'vibe'> = ['', 'trip', 'vibe'];

export function Feed() {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<'' | 'trip' | 'vibe'>('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<Env>({
    queryKey: ['/admin/api/feed', page, kind],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (kind) p.set('kind', kind);
      return api.get<Env>(`/admin/api/feed?${p}`);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/api/feed/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/feed'] }),
  });

  return (
    <div>
      <PageHeader title="Feed" subtitle={data ? `${fmtN(data.total)} posts` : 'Loading…'} />

      <div className="ad-card p-3 mb-3 flex gap-1">
        {KINDS.map((k) => (
          <button
            key={k || 'all'}
            onClick={() => {
              setKind(k);
              setPage(1);
            }}
            className={cn(
              'ad-btn-sm',
              kind === k ? 'bg-accent/20 text-accent border border-accent/40' : 'ad-btn-secondary',
            )}
          >
            {k || 'All'}
          </button>
        ))}
      </div>

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Author</th>
              <th>Kind</th>
              <th>Likes</th>
              <th>Comments</th>
              <th>Published</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : (
              data?.rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.coverImageUrl && (
                      <img
                        src={p.coverImageUrl}
                        alt=""
                        className="w-12 h-12 rounded object-cover float-left mr-3"
                      />
                    )}
                    <div className="text-sm line-clamp-2">{p.caption ?? <span className="text-text3 italic">No caption</span>}</div>
                  </td>
                  <td>
                    <div>{p.authorName}</div>
                    <div className="text-text3 text-xs">{p.authorEmail}</div>
                  </td>
                  <td>
                    <span className={cn('ad-badge', p.kind === 'trip' ? 'ad-badge-accent' : 'ad-badge-neutral')}>
                      {p.kind}
                    </span>
                  </td>
                  <td className="text-text2">{p.likeCount}</td>
                  <td className="text-text2">{p.commentCount}</td>
                  <td className="text-text2">{fmtRelative(p.publishedAt)}</td>
                  <td className="text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Delete this post permanently?`)) del.mutate(p.id);
                      }}
                      className="text-error hover:underline text-xs"
                    >
                      <Trash2 size={12} className="inline" /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
      )}
    </div>
  );
}
